import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Brain, Users, Mic, Phone, HeartHandshake, Sprout, Sun } from 'lucide-react';
import { useI3Modal } from '../context/I3ModalContext';
import { VideoPlaceholder } from '../../../components/MediaPlaceholder';
import { lnAssets } from '../utils/lnAssets';
import {
  SITE_FULL_NAME,
  SITE_HERO_HEADLINE,
  SITE_HERO_KICKER,
  SITE_HERO_LEAD,
  SITE_HERO_TAGLINE,
  SITE_PRACTITIONER_CREDENTIALS,
  SITE_PRACTITIONER_NAME,
} from '../sitePlaceholders';
import LisaPortrait from '../components/LisaPortrait';
import I3TestimonialsCarousel from '../components/I3TestimonialsCarousel';
import { useRevealSections } from '../hooks/useReveal';
import { BOOK_SESSION_PATH } from '../bookingConfig';
import '../styles/I3Home.css';
import '../styles/I3BridgeButtons.css';

const SERVICES = [
  {
    num: '01',
    icon: Heart,
    title: 'Marriage & Couples Therapy',
    summary:
      'Counseling for dating, engaged, and married couples—communication, conflict, trust, and reconnection.',
    modal: 'marriage' as const,
  },
  {
    num: '02',
    icon: Brain,
    title: 'Individual Therapy',
    summary: 'One-on-one support for anxiety, depression, stress, grief, life transitions, and personal growth.',
    modal: 'leadership' as const,
  },
  {
    num: '03',
    icon: Users,
    title: 'Family Counseling',
    summary: 'Family sessions that improve boundaries, parenting alignment, co-parenting, and blended-family dynamics.',
    modal: 'financial' as const,
  },
  {
    num: '04',
    icon: Mic,
    title: 'Groups & Workshops',
    summary:
      'Occasional premarital workshops, marriage-focused groups, and faith-informed presentations—ask what I\u2019m offering now.',
    modal: 'speaking' as const,
  },
] as const;

const JOURNEY_STEPS = [
  { icon: Phone, title: 'Reach Out', line: 'Send a message or book online when you\'re ready.' },
  { icon: HeartHandshake, title: 'Connect', line: 'We\'ll talk about what you\'re facing and what you need.' },
  { icon: Heart, title: 'Heal', line: 'Work through patterns with steady, compassionate support.' },
  { icon: Sprout, title: 'Grow', line: 'Build skills and insight that hold up outside session.' },
  { icon: Sun, title: 'Thrive', line: 'Move toward connection, clarity, and lasting wellness.' },
] as const;

const I3Home: React.FC = () => {
  const { openModal } = useI3Modal();
  const homeRef = useRef<HTMLDivElement>(null);
  useRevealSections(homeRef);

  return (
    <div className="i3-home i3-home--mockup" ref={homeRef}>
      {/* Hero */}
      <section className="i3-hero i3-hero-with-video i3-hero--mockup">
        <div className="i3-hero-backdrop" aria-hidden>
          <VideoPlaceholder
            className="i3-hero-backdrop-video i3-hero-backdrop-video--ocean"
            src={lnAssets.videos.landing}
            label="The Bridge — calm sunrise beach background"
            playbackRate={0.78}
          />
          <div className="i3-hero-backdrop-scrim i3-hero-backdrop-scrim--sunrise" aria-hidden />
        </div>
        <div className="i3-hero-content i3-hero-content--mockup">
          <div className="i3-hero-copy-mockup">
            <p className="i3-hero-kicker">{SITE_HERO_KICKER}</p>
            <h1 className="i3-hero-headline-mockup">{SITE_HERO_HEADLINE}</h1>
            <div className="i3-hero-copy-rule" aria-hidden />
            <p className="i3-hero-lead-mockup">{SITE_HERO_LEAD}</p>
            <div className="i3-hero-action-block">
              <p className="i3-hero-tagline-mockup" aria-label="Heal, Grow, Thrive">
                {SITE_HERO_TAGLINE.map((word, i) => (
                  <React.Fragment key={word}>
                    <span className="i3-hero-tagline-mockup-word">{word.toUpperCase()}</span>
                    {i < SITE_HERO_TAGLINE.length - 1 ? (
                      <span className="i3-hero-tagline-mockup-sep" aria-hidden>
                        ·
                      </span>
                    ) : null}
                  </React.Fragment>
                ))}
              </p>
              <div className="i3-hero-ctas i3-hero-ctas-slide-in i3-hero-ctas--mockup">
                <Link to="/about" className="i3-bridge-btn i3-bridge-btn--slate">
                  Start Your Journey
                </Link>
                <Link to={BOOK_SESSION_PATH} className="i3-bridge-btn i3-bridge-btn--sand">
                  Book a Session
                </Link>
              </div>
            </div>
          </div>
          <h1 className="i3-hero-sr-only">{SITE_FULL_NAME}</h1>
        </div>
      </section>

      {/* Meet Lisa J Norman */}
      <section className="i3-section i3-founders i3-founders-band i3-home-section-bleed" aria-labelledby="i3-founders-title">
        <div className="i3-founders-inner">
          <div className="i3-founders-copy">
            <p className="i3-founders-kicker">Your therapist</p>
            <h2 id="i3-founders-title">Meet {SITE_PRACTITIONER_NAME}</h2>
            <p className="i3-founders-credentials">{SITE_PRACTITIONER_CREDENTIALS}</p>
            <p className="i3-founders-intro">
              Lisa is a licensed therapist in the state of Texas who is passionate about helping people navigate
              life with greater clarity, the proper tools, and more insight. Lisa believes that good therapy works.
            </p>
            <ul className="i3-founders-highlights i3-founders-highlights--check">
              <li>Customized individual therapy</li>
              <li>Marriage and Family Therapy</li>
              <li>Parent education and support</li>
              <li>Children therapy</li>
              <li>Conflict resolution</li>
            </ul>
            <div className="i3-founders-actions">
              <Link to="/about" className="i3-cta i3-cta-primary">
                About my practice
              </Link>
              <Link to="/contact" className="i3-cta i3-cta-secondary">
                Get in touch
              </Link>
            </div>
          </div>
          <div className="i3-founders-visual i3-founders-visual--portrait">
            <LisaPortrait />
          </div>
        </div>
      </section>

      {/* How I Can Help */}
      <section className="i3-section i3-what-we-do i3-home-section-bleed" aria-labelledby="i3-what-we-do-title">
        <div className="i3-home-bleed-header i3-home-bleed-header--center">
          <p className="i3-what-we-do-eyebrow">Areas of focus</p>
          <h2 id="i3-what-we-do-title" className="i3-what-we-do-headline">
            How I can help
          </h2>
        </div>
        <div className="i3-pillars-row i3-pillars-row--mockup">
          {SERVICES.map(({ num, icon: Icon, title, summary, modal }) => (
            <article key={num} className="i3-service-card-mockup i3-reveal">
              <div className="i3-service-card-mockup-head">
                <span className="i3-service-card-mockup-icon" aria-hidden>
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <span className="i3-service-card-mockup-num" aria-hidden>
                  {num}
                </span>
              </div>
              <h3>{title}</h3>
              <p>{summary}</p>
              <button type="button" className="i3-service-card-mockup-link" onClick={() => openModal(modal)}>
                Learn more →
              </button>
            </article>
          ))}
        </div>
        <div className="i3-pillars-footer">
          <Link to="/services" className="i3-pillars-view-services">
            View all services
          </Link>
        </div>
      </section>

      {/* Your Journey Matters */}
      <section className="i3-journey i3-home-section-bleed i3-reveal" aria-labelledby="i3-journey-title">
        <div className="i3-journey-backdrop" aria-hidden />
        <div className="i3-journey-inner">
          <div className="i3-journey-copy">
            <p className="i3-journey-eyebrow">A bridge toward healing</p>
            <h2 id="i3-journey-title" className="i3-journey-title">
              Your Journey Matters
            </h2>
            <p className="i3-journey-lead">
              Therapy is a collaborative process. Together, we&apos;ll identify what you&apos;re carrying, name
              what needs to change, and build a path forward—at a pace that honors your story and your strength.
            </p>
            <Link to="/about" className="i3-cta i3-cta-primary">
              Learn more about my approach
            </Link>
          </div>
          <ol className="i3-journey-steps" aria-label="Therapy journey steps">
            {JOURNEY_STEPS.map(({ icon: Icon, title, line }, i) => (
              <li key={title} className="i3-journey-step">
                <div className="i3-journey-step-icon-wrap">
                  <Icon size={20} strokeWidth={1.75} aria-hidden />
                </div>
                <span className="i3-journey-step-num">{i + 1}</span>
                <strong className="i3-journey-step-title">{title}</strong>
                <p className="i3-journey-step-line">{line}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <I3TestimonialsCarousel />

      {/* Final CTA */}
      <section className="i3-final-cta i3-home-section-bleed i3-reveal" aria-labelledby="i3-final-cta-title">
        <div className="i3-final-cta-deco i3-final-cta-deco--left" aria-hidden />
        <div className="i3-final-cta-deco i3-final-cta-deco--right" aria-hidden />
        <div className="i3-final-cta-inner">
          <h2 id="i3-final-cta-title" className="i3-final-cta-title">
            Ready to Take the First Step?
          </h2>
          <p className="i3-final-cta-lead">
            You don&apos;t have to navigate this journey alone. I&apos;m here to help.
          </p>
          <div className="i3-final-cta-actions">
            <Link to={BOOK_SESSION_PATH} className="i3-bridge-btn i3-bridge-btn--sand">
              Book a Session
            </Link>
            <Link to="/contact" className="i3-bridge-btn i3-bridge-btn--sand">
              Get in touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default I3Home;
